// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Notify from "@guestsupply/App/Components/Notify/Notify";
import ProductActions, { ProductActionsStyle } from "@guestsupply/App/Components/Product/ProductActions";
import updateHotspotProduct from "@guestsupply/App/Handlers/FindProductByErp/UpdateHotspotProduct";
import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import ProductAddToCartButton from "@guestsupply/Overrides/Components/ProductAddToCartButton";
import ProductQuantityOrdered, {
    ProductQuantityStyles,
} from "@guestsupply/Overrides/Components/ProductQuantityOrdered";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    product: ProductModel;
}

const mapStateToProps = (state: CustomAppState, props: OwnProps) => {
    return {
        productInfo: state.xHotspotProduct.productsInfo[props.product.productNumber],
    };
};

const mapDispatchToProps = {
    updateHotspotProduct,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface HotspotProductStyles {
    wrapper?: InjectableCss;
    spinnerWrapper?: InjectableCss;
    productImage?: ProductImageStyles;
    descriptionWrapper?: InjectableCss;
    productDescription?: ProductDescriptionStyles;
    partNumberStyle?: SmallHeadingAndTextStyles;
    productPrice?: ProductPriceStyles;
    productActionsStyle?: ProductActionsStyle;
    notify?: TypographyProps;
}

export const styles: HotspotProductStyles = {};

const HotspotProduct: React.FC<Props> = ({ product, updateHotspotProduct, productInfo }) => {
    if (!product || !productInfo) {
        return (
            <Notify type="warning" {...styles.notify}>
                {translate("Product Not Found")}
            </Notify>
        );
    }

    const updateQuantity = (qty: number) => {
        updateHotspotProduct({ productErp: product.productNumber, qtyOrdered: qty, isQty: true });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateHotspotProduct({ productErp: product.productNumber, uom: unitOfMeasure, isQty: false });
    };

    const productContext = {
        product,
        productInfo,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };

    return (
        <ProductContext.Provider value={productContext}>
            <StyledWrapper {...styles.wrapper}>
                <ProductImage
                    product={productContext as any}
                    extendedStyles={styles.productImage}
                    {...{ isGridView: false }}
                />
                <StyledWrapper {...styles.descriptionWrapper}>
                    <ProductDescription product={productContext} extendedStyles={styles.productDescription} />
                    <SmallHeadingAndText
                        extendedStyles={styles.partNumberStyle}
                        heading={translate("Part Number")}
                        text={product.productNumber}
                    ></SmallHeadingAndText>
                    <ProductPrice
                        showLabel={false}
                        product={productContext}
                        showPack={false}
                        extendedStyles={styles.productPrice}
                    />
                    <ProductActions iconOnly={true} extendedStyles={styles.productActionsStyle} />
                </StyledWrapper>
            </StyledWrapper>
        </ProductContext.Provider>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(HotspotProduct);
